import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Copy } from "@phosphor-icons/react";
import { CloseIcon } from "../../../assets/icons";
import {
  Overlay,
  Modal,
  CloseButton,
  Header,
} from "../../../components/UploadButton/uploadButtonModals/SelectTemplateStyled";
import {
  createGeneratedDocumentFolder,
  deleteGeneratedDocument,
  deleteGeneratedDocumentFolder,
  fetchGeneratedDocumentDownloadUrl,
  fetchGeneratedDocumentFolders,
  fetchGeneratedFolderDocuments,
  moveGeneratedDocument,
  renameGeneratedDocument,
  updateGeneratedDocumentFolder,
} from "../../../service/settings";

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 18px;
  min-height: 520px;
`;

const Panel = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px;
  min-width: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 15px;
  color: #111827;
`;

const Button = styled.button`
  border: 1px solid ${(p) => (p.$danger ? "#fca5a5" : "#d1d5db")};
  background: ${(p) => (p.$primary ? "#0C53BB" : "#fff")};
  color: ${(p) => (p.$primary ? "#fff" : p.$danger ? "#dc2626" : "#374151")};
  border-radius: 7px;
  padding: 7px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const FolderButton = styled.div`
  width: 100%;
  border: 1px solid ${(p) => (p.$active ? "#0C53BB" : "#e5e7eb")};
  background: ${(p) => (p.$active ? "#eff6ff" : "#fff")};
  color: #111827;
  border-radius: 8px;
  padding: 10px;
  text-align: left;
  cursor: pointer;
  margin-bottom: 8px;
`;

const FolderName = styled.div`
  font-size: 13px;
  font-weight: 700;
`;

const Meta = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 3px;
`;

const FolderActions = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 8px;
`;

const Table = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 1.4fr) minmax(140px, 1fr) minmax(110px, 0.7fr) auto;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid #f3f4f6;
  &:last-child {
    border-bottom: 0;
  }
`;

const HeaderCell = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Cell = styled.div`
  min-width: 0;
  font-size: 13px;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const Empty = styled.div`
  padding: 24px;
  text-align: center;
  color: #6b7280;
  font-size: 13px;
`;

const Select = styled.select`
  border: 1px solid #d1d5db;
  border-radius: 7px;
  padding: 6px 8px;
  font-size: 12px;
`;

const FolderModal = styled(Modal)`
  width: 460px;
  max-width: calc(100vw - 32px);
`;

const ModalBody = styled.form`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ModalLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${(p) => (p.$error ? "#dc2626" : "#d1d5db")};
  border-radius: 8px;
  font-size: 13px;
  outline: none;
  &:focus {
    border-color: ${(p) => (p.$error ? "#dc2626" : "#0C53BB")};
    box-shadow: 0 0 0 2px ${(p) => (p.$error ? "rgba(220, 38, 38, 0.12)" : "rgba(37, 99, 235, 0.1)")};
  }
`;

const ModalError = styled.div`
  color: #dc2626;
  font-size: 12px;
  line-height: 1.4;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
`;

const Toast = styled.div`
  position: fixed;
  top: 18px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1600;
  width: min(920px, calc(100vw - 32px));
  display: grid;
  grid-template-columns: 4px minmax(130px, auto) minmax(0, 1fr) auto auto;
  gap: 10px;
  align-items: center;
  padding: 10px 12px 10px 0;
  background: #fff;
  border: 1px solid #d8dde6;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(9, 30, 66, 0.18);
  font-family: "Manrope", sans-serif;
`;

const ToastBar = styled.div`
  width: 4px;
  height: 100%;
  min-height: 34px;
  background: #ba0517;
  border-radius: 4px 0 0 4px;
`;

const ToastTitle = styled.div`
  color: #181818;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
`;

const ToastMessage = styled.div`
  color: #3e3e3c;
  font-size: 12px;
  line-height: 1.35;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ToastIconButton = styled.button`
  border: 0;
  background: transparent;
  color: #706e6b;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 4px;
  font-size: 18px;
  line-height: 1;
  padding: 0;
  &:hover {
    background: #f3f2f2;
    color: #181818;
  }
`;

const normalizeFolders = (response) => {
  const data = response?.data || response || [];
  return Array.isArray(data) ? data : data.results || data.folders || [];
};

const documentId = (doc) => doc?.document_id || doc?.id;

const fieldLabel = (field) =>
  String(field)
    .replace(/^_+|_+$/g, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase()) || "Error";

const tryParseJsonString = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed || (!trimmed.startsWith("{") && !trimmed.startsWith("["))) return value;
  try {
    return JSON.parse(trimmed);
  } catch (_err) {
    return value;
  }
};

const normalizeErrorValue = (value) => {
  if (!value) return "";
  const parsedValue = tryParseJsonString(value);
  if (parsedValue !== value) return normalizeErrorValue(parsedValue);
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    return value.map(normalizeErrorValue).filter(Boolean).join(" ");
  }
  if (typeof value === "object") {
    return Object.entries(value)
      .map(([key, nestedValue]) => {
        const message = normalizeErrorValue(nestedValue);
        if (!message) return "";
        return `${fieldLabel(key)}: ${message}`;
      })
      .filter(Boolean)
      .join(" ");
  }
  return String(value);
};

const summarizeToastMessage = (message) => {
  const plain = String(message || "")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!plain) return "Open the copied error details for more information.";
  return plain.length > 180 ? `${plain.slice(0, 177)}...` : plain;
};

const getGeneratedDocumentsErrorMessage = (err, fallback) => {
  const data = err?.response?.data;
  if (data) {
    if (typeof data === "string") return normalizeErrorValue(data);

    const primaryMessage = normalizeErrorValue(
      data.error || data.detail || data.details || data.message
    );
    if (primaryMessage) return primaryMessage;

    const fieldMessages = normalizeErrorValue(data);
    if (fieldMessages) return fieldMessages;
  }

  const status = err?.response?.status;
  if (status === 401 || status === 403) {
    return "You do not have permission to perform this action.";
  }
  if (!err?.response) {
    return "Could not reach DMS. Please check your connection and try again.";
  }

  return fallback;
};

export default function GeneratedDocuments() {
  const [folders, setFolders] = useState([]);
  const [activeFolderId, setActiveFolderId] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [folderModal, setFolderModal] = useState({
    open: false,
    mode: "create",
    folder: null,
    name: "",
    error: "",
    saving: false,
  });

  const activeFolder = useMemo(
    () => folders.find((folder) => String(folder.id) === String(activeFolderId)),
    [folders, activeFolderId]
  );

  const showErrorToast = useCallback((message, title = "We couldn't save this change") => {
    setToast({
      id: Date.now().toString(),
      title,
      message,
      summary: summarizeToastMessage(message),
    });
  }, []);

  const loadFolders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchGeneratedDocumentFolders();
      const nextFolders = normalizeFolders(response);
      setFolders(nextFolders);
      setActiveFolderId((current) => current || nextFolders[0]?.id || null);
    } catch (err) {
      console.error("Failed to load generated document folders", err);
      const message = getGeneratedDocumentsErrorMessage(err, "Could not load Generated Documents folders.");
      showErrorToast(message, "Generated Documents could not load");
    } finally {
      setLoading(false);
    }
  }, [showErrorToast]);

  const loadDocuments = useCallback(async (folderId) => {
    if (!folderId) {
      setDocuments([]);
      return;
    }
    setLoading(true);
    try {
      const response = await fetchGeneratedFolderDocuments(folderId);
      setDocuments(response?.data?.documents || []);
    } catch (err) {
      console.error("Failed to load generated documents", err);
      const message = getGeneratedDocumentsErrorMessage(err, "Could not load generated documents for this folder.");
      showErrorToast(message, "Folder documents could not load");
    } finally {
      setLoading(false);
    }
  }, [showErrorToast]);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  useEffect(() => {
    loadDocuments(activeFolderId);
  }, [activeFolderId, loadDocuments]);

  const openFolderModal = (mode, folder = null) => {
    setFolderModal({
      open: true,
      mode,
      folder,
      name: folder?.name || "",
      error: "",
      saving: false,
    });
  };

  const closeFolderModal = () => {
    setFolderModal((current) => ({ ...current, open: false, error: "", saving: false }));
  };

  const updateFolderModalName = (name) => {
    setFolderModal((current) => ({ ...current, name, error: "" }));
  };

  const submitFolderModal = async (event) => {
    event.preventDefault();
    const name = folderModal.name.trim();
    if (!name) {
      setFolderModal((current) => ({ ...current, error: "Folder name is required." }));
      return;
    }

    if (folderModal.mode === "edit" && name === folderModal.folder?.name) {
      closeFolderModal();
      return;
    }

    setFolderModal((current) => ({ ...current, saving: true, error: "" }));
    try {
      const response =
        folderModal.mode === "edit"
          ? await updateGeneratedDocumentFolder(folderModal.folder.id, { name })
          : await createGeneratedDocumentFolder({ name });
      await loadFolders();
      if (folderModal.mode === "create") {
        setActiveFolderId(response?.data?.id || null);
      }
      closeFolderModal();
    } catch (err) {
      const fallback =
        folderModal.mode === "edit" ? "Could not rename folder." : "Could not create folder.";
      const message = getGeneratedDocumentsErrorMessage(err, fallback);
      console.error(`Failed to ${folderModal.mode === "edit" ? "rename" : "create"} generated document folder`, err);
      showErrorToast(
        message,
        folderModal.mode === "edit" ? "Folder could not be renamed" : "Folder could not be created"
      );
      setFolderModal((current) => ({ ...current, saving: false, error: "" }));
    }
  };

  const handleDeleteFolder = async (folder) => {
    if (!window.confirm(`Delete generated folder "${folder.name}"? Existing DMS documents will not be deleted.`)) return;
    try {
      await deleteGeneratedDocumentFolder(folder.id);
      await loadFolders();
      setActiveFolderId(null);
    } catch (err) {
      console.error("Failed to delete generated document folder", err);
      const message = getGeneratedDocumentsErrorMessage(err, "Could not delete folder.");
      showErrorToast(message, "Folder could not be deleted");
    }
  };

  const openDocument = (doc, mode) => {
    const id = documentId(doc);
    if (!doc?.matter_id || !id) return;
    const suffix = mode === "edit" ? "?edit=1" : "";
    window.open(`/matter/${doc.matter_id}/D${id}${suffix}`, "_blank", "noopener,noreferrer");
  };

  const handleDownload = async (doc) => {
    try {
      const response = await fetchGeneratedDocumentDownloadUrl(documentId(doc));
      const data = response?.data || response;
      const url = data?.url || data?.fallback_stream_url;
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Failed to preview generated document", err);
      const message = getGeneratedDocumentsErrorMessage(err, "Could not open preview for this document.");
      showErrorToast(message, "Preview could not open");
    }
  };

  const handleRenameDocument = async (doc) => {
    const name = window.prompt("Document name", doc.document_name)?.trim();
    if (!name || name === doc.document_name) return;
    try {
      await renameGeneratedDocument(documentId(doc), name);
      await loadDocuments(activeFolderId);
    } catch (err) {
      console.error("Failed to rename generated document", err);
      const message = getGeneratedDocumentsErrorMessage(err, "Could not rename document.");
      showErrorToast(message, "Document could not be renamed");
    }
  };

  const handleDeleteDocument = async (doc) => {
    if (!window.confirm(`Delete "${doc.document_name}"?`)) return;
    try {
      await deleteGeneratedDocument(documentId(doc));
      await loadDocuments(activeFolderId);
    } catch (err) {
      console.error("Failed to delete generated document", err);
      const message = getGeneratedDocumentsErrorMessage(err, "Could not delete document.");
      showErrorToast(message, "Document could not be deleted");
    }
  };

  const handleMoveDocument = async (doc, targetFolderId) => {
    if (!targetFolderId || String(targetFolderId) === String(doc.folder_template_id)) return;
    try {
      await moveGeneratedDocument(documentId(doc), targetFolderId);
      await loadDocuments(activeFolderId);
      await loadFolders();
    } catch (err) {
      console.error("Failed to move generated document", err);
      const message = getGeneratedDocumentsErrorMessage(err, "Could not move document.");
      showErrorToast(message, "Document could not be moved");
    }
  };

  return (
    <Wrapper>
      <Panel>
        <HeaderRow>
          <Title>Folders</Title>
          <Button type="button" $primary onClick={() => openFolderModal("create")}>
            New folder
          </Button>
        </HeaderRow>
        {folders.length === 0 && <Empty>No generated-document folders yet.</Empty>}
        {folders.map((folder) => (
          <FolderButton
            key={folder.id}
            role="button"
            tabIndex={0}
            $active={String(activeFolderId) === String(folder.id)}
            onClick={() => setActiveFolderId(folder.id)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setActiveFolderId(folder.id);
              }
            }}
          >
            <FolderName>{folder.name}</FolderName>
            <Meta>{folder.document_count || 0} generated document{folder.document_count === 1 ? "" : "s"}</Meta>
            <FolderActions>
              <Button type="button" onClick={(event) => {
                event.stopPropagation();
                openFolderModal("edit", folder);
              }}>
                Edit
              </Button>
              <Button type="button" $danger onClick={(event) => {
                event.stopPropagation();
                handleDeleteFolder(folder);
              }}>
                Delete
              </Button>
            </FolderActions>
          </FolderButton>
        ))}
      </Panel>

      <Panel>
        <HeaderRow>
          <Title>{activeFolder ? activeFolder.name : "Generated Documents"}</Title>
          <Meta>{loading ? "Loading..." : `${documents.length} document${documents.length === 1 ? "" : "s"}`}</Meta>
        </HeaderRow>
        {!activeFolderId && <Empty>Select or create a folder to see generated documents.</Empty>}
        {activeFolderId && documents.length === 0 && !loading && (
          <Empty>No documents have been generated into this folder yet.</Empty>
        )}
        {documents.length > 0 && (
          <Table>
            <Row>
              <HeaderCell>Document</HeaderCell>
              <HeaderCell>Matter</HeaderCell>
              <HeaderCell>Package</HeaderCell>
              <HeaderCell>Actions</HeaderCell>
            </Row>
            {documents.map((doc) => (
              <Row key={doc.id || doc.document_id}>
                <Cell title={doc.document_name}>{doc.document_name}</Cell>
                <Cell title={doc.matter_name}>{doc.matter_name || doc.matter_id}</Cell>
                <Cell title={doc.package_name}>{doc.package_name || "Package"}</Cell>
                <ActionGroup>
                  <Button type="button" onClick={() => openDocument(doc, "edit")}>Edit</Button>
                  <Button type="button" onClick={() => handleDownload(doc)}>Preview</Button>
                  <Button type="button" onClick={() => handleRenameDocument(doc)}>Rename</Button>
                  <Select
                    value={doc.folder_template_id || ""}
                    onChange={(event) => handleMoveDocument(doc, event.target.value)}
                    aria-label={`Move ${doc.document_name}`}
                  >
                    {folders.map((folder) => (
                      <option key={folder.id} value={folder.id}>{folder.name}</option>
                    ))}
                  </Select>
                  <Button type="button" $danger onClick={() => handleDeleteDocument(doc)}>Delete</Button>
                </ActionGroup>
              </Row>
            ))}
          </Table>
        )}
      </Panel>
      {folderModal.open && (
        <Overlay>
          <FolderModal>
            <CloseButton onClick={closeFolderModal} aria-label="Close folder modal">
              <CloseIcon />
            </CloseButton>
            <Header>{folderModal.mode === "edit" ? "Edit Folder" : "Create Folder"}</Header>
            <ModalBody onSubmit={submitFolderModal}>
              <ModalLabel htmlFor="generated-document-folder-name">Folder Name</ModalLabel>
              <ModalInput
                id="generated-document-folder-name"
                value={folderModal.name}
                onChange={(event) => updateFolderModalName(event.target.value)}
                placeholder="e.g. Client contracts"
                autoFocus
                $error={Boolean(folderModal.error)}
              />
              {folderModal.error && <ModalError>{folderModal.error}</ModalError>}
              <ModalFooter>
                <Button type="button" onClick={closeFolderModal} disabled={folderModal.saving}>
                  Cancel
                </Button>
                <Button type="submit" $primary disabled={folderModal.saving}>
                  {folderModal.saving
                    ? "Saving..."
                    : folderModal.mode === "edit"
                      ? "Update Folder"
                      : "Create Folder"}
                </Button>
              </ModalFooter>
            </ModalBody>
          </FolderModal>
        </Overlay>
      )}
      {toast && (
        <Toast role="alert" aria-live="assertive">
          <ToastBar />
          <ToastTitle>{toast.title}</ToastTitle>
          <ToastMessage title={toast.message}>{toast.summary}</ToastMessage>
          <ToastIconButton
            type="button"
            aria-label="Copy full error"
            title="Copy full error"
            onClick={() => navigator.clipboard?.writeText?.(toast.message)}
          >
            <Copy size={16} weight="bold" />
          </ToastIconButton>
          <ToastIconButton type="button" aria-label="Dismiss notification" onClick={() => setToast(null)}>
            &times;
          </ToastIconButton>
        </Toast>
      )}
    </Wrapper>
  );
}
